/**
 * Reports API client (read-only, V1).
 */
import { apiFetch } from '../apiFetch'

export type PoWorkflowStatus = 'APPROVED' | 'SENT' | 'PARTIAL' | 'SHORT' | string
export type PoDeliveryStatus = 'NOT_RECEIVED' | 'PARTIALLY_RECEIVED' | 'FULLY_RECEIVED'

export type OpenPoLine = {
  id: string
  sort_order: number
  material_id: string
  material_code: string | null
  material_name: string
  spec_text: string | null
  order_uom: string
  ordered_qty: number
  received_qty: number
  remaining_qty: number
  short_qty: number
  short_resolution: string | null
  delivery_status: PoDeliveryStatus
  line_total: number
  requirement_line_id: string | null
}

export type OpenPoRow = {
  id: string
  code: string
  supplier_id: string
  supplier_name: string
  workflow_status: PoWorkflowStatus
  delivery_status: PoDeliveryStatus
  is_overdue: boolean
  delivery_date: string
  currency: string
  line_count: number
  total_amount: number
  received_pct: number
  lines: OpenPoLine[]
}

export type OpenPoSummary = {
  po_count: number
  overdue_count: number
  not_received_count: number
  partially_received_count: number
  total_amount: number
}

export type OpenPoReport = {
  items: OpenPoRow[]
  summary: OpenPoSummary
}

export type OpenPoReportParams = {
  q?: string
  overdueOnly?: boolean
  supplierId?: string
  includeFullyReceived?: boolean
}

export async function fetchOpenPurchaseOrdersReportApi(
  params: OpenPoReportParams = {},
): Promise<OpenPoReport> {
  const qs = new URLSearchParams()
  if (params.q && params.q.trim()) qs.set('q', params.q.trim())
  if (params.overdueOnly) qs.set('overdue_only', 'true')
  if (params.supplierId) qs.set('supplier_id', params.supplierId)
  if (params.includeFullyReceived) qs.set('include_fully_received', 'true')
  const suffix = qs.toString()
  const res = await apiFetch(`/api/reports/open-purchase-orders${suffix ? `?${suffix}` : ''}`)
  if (!res.ok) {
    let message = `Không thể tải báo cáo PO đang mở (${res.status})`
    try {
      const body = (await res.json()) as { error?: { message?: string } }
      if (body?.error?.message) message = body.error.message
    } catch {
      // ignore non-JSON error body
    }
    throw new Error(message)
  }
  const body = (await res.json()) as { data?: OpenPoRow[]; summary?: OpenPoSummary }
  return {
    items: body.data ?? [],
    summary: body.summary ?? {
      po_count: 0,
      overdue_count: 0,
      not_received_count: 0,
      partially_received_count: 0,
      total_amount: 0,
    },
  }
}

export type PackagingPricingReportLine = {
  id: string
  itemName: string
  loai: string
  kt1: number | string | null
  kt2: number | string | null
  kt3: number | string | null
  kt4: number | string | null
  dinhLuong: number | string | null
  dvt: string
  sl: number
  supplierId: string | null
  supplierName: string | null
  unitPrice: number | null
  lineTotal: number | null
  vatRate: number | null
  vatAmount: number | null
  lineTotalWithVat: number | null
  priceSource: string
  priceStatus: string
  notes: string
}

export type PackagingPricingReportConfigItem = {
  configId: string
  productId: string
  productCode: string
  productName: string
  customerCode: string | null
  assemblyType: string
  packagingType: string
  status: string
  combinationLabel: string
  cartonSummary?: string
  lines: PackagingPricingReportLine[]
  totalMaterialCost: number
  effectiveVatRate: number
  totalVatAmount: number
  grandTotalCostWithVat: number
}

export type PackagingPricingReportData = {
  items: PackagingPricingReportConfigItem[]
  summary: {
    totalConfigs: number
    totalProducts: number
    totalMaterialCost: number
    totalVatAmount: number
    grandTotalCostWithVat: number
  }
}

export async function fetchPackagingPricingReportApi(params: {
  productId?: string
  q?: string
} = {}): Promise<PackagingPricingReportData> {
  const qs = new URLSearchParams()
  if (params.productId) qs.set('product_id', params.productId)
  if (params.q && params.q.trim()) qs.set('q', params.q.trim())
  const suffix = qs.toString()
  const res = await apiFetch(`/api/reports/packaging-pricing${suffix ? `?${suffix}` : ''}`)
  if (!res.ok) {
    throw new Error(`Không thể tải báo cáo giá đóng gói (${res.status})`)
  }
  const body = (await res.json()) as { data?: PackagingPricingReportData }
  return body.data ?? {
    items: [],
    summary: { totalConfigs: 0, totalProducts: 0, totalMaterialCost: 0, totalVatAmount: 0, grandTotalCostWithVat: 0 },
  }
}

export type ProductBomCostReportItem = {
  productId: string
  productCode: string
  productName: string
  customerCode: string | null
  imageUrl: string | null
  combinationLabel: string
  packagingCost: number
  hardwareCost: number
  totalCost: number
  hardwareUnmappedCount: number
  packagingLinesCount: number
  hardwareLinesCount: number
}

export type ProductBomCostReportData = {
  items: ProductBomCostReportItem[]
  summary: {
    totalRows: number
    totalProducts: number
    grandPackagingTotal: number
    grandHardwareTotal: number
    grandCombinedTotal: number
    grandHardwareUnmappedCount: number
  }
}

export async function fetchProductBomCostReportApi(params: {
  q?: string
} = {}): Promise<ProductBomCostReportData> {
  const qs = new URLSearchParams()
  if (params.q && params.q.trim()) qs.set('q', params.q.trim())
  const suffix = qs.toString()
  const res = await apiFetch(`/api/reports/product-bom-costs${suffix ? `?${suffix}` : ''}`)
  if (!res.ok) {
    throw new Error(`Không thể tải báo cáo chi phí đóng gói & ngũ kim (${res.status})`)
  }
  const body = (await res.json()) as { data?: ProductBomCostReportData }
  return body.data ?? {
    items: [],
    summary: { totalRows: 0, totalProducts: 0, grandPackagingTotal: 0, grandHardwareTotal: 0, grandCombinedTotal: 0, grandHardwareUnmappedCount: 0 },
  }
}
